/**
 * Reusable function for one true form
 */

import { QuoteLineItemProps } from '@partsbadger/types';

export const fieldName = (name: string, item_id: number) => {
    return name + '__' + item_id;
};

const CUSTOM_SPEC_NAME = 'Custom';

const isCustomLabel = (name?: string | null) => name === CUSTOM_SPEC_NAME;

/** Product.finishes / coatings are typed as the empty tuple `[]` in QuoteInterfaces. */
const firstStringSpec = (values: unknown): string | undefined => {
    if (!Array.isArray(values) || values.length === 0) {
        return undefined;
    }
    const first = (values as unknown[])[0];
    return typeof first === 'string' ? first : undefined;
};

const matchesCustomCatalogId = (
    selectedId: string | number | null | undefined,
    catalog: Array<{ id: number; name: string }> | undefined
) => {
    if (selectedId == null || !catalog?.length) {
        return false;
    }
    const customId = catalog.find(entry => entry.name === CUSTOM_SPEC_NAME)?.id;
    return customId != null && Number(selectedId) === Number(customId);
};

/**
 * Custom specs can be selected in the dropdown (catalog id) while
 * `product.material` / `material_related.name` are still empty. The extra
 * "Enter Custom …" input must follow the selection, not only the string name.
 */
export const isCustomMaterialSelected = (
    item: QuoteLineItemProps,
    materials?: Array<{ id: number; name: string }>,
    selected?: { key?: string | number; label?: string } | null
) => {
    const selectedLabel = selected?.label;
    return (
        isCustomLabel(item.product?.material) ||
        isCustomLabel(item.product?.material_related?.name) ||
        isCustomLabel(item.material?.name) ||
        isCustomLabel(selectedLabel) ||
        matchesCustomCatalogId(selected?.key, materials) ||
        matchesCustomCatalogId(item.product?.material_related?.id, materials) ||
        matchesCustomCatalogId(item.material?.id, materials)
    );
};

export const isCustomFinishSelected = (
    item: QuoteLineItemProps,
    finishes?: Array<{ id: number; name: string }>,
    selected?: { key?: string | number; label?: string } | null
) => {
    const selectedLabel = selected?.label;
    const finishName = firstStringSpec(item.product?.finishes);
    return (
        isCustomLabel(finishName) ||
        isCustomLabel(item.product?.finish_related?.name) ||
        isCustomLabel(item.finish?.name) ||
        isCustomLabel(selectedLabel) ||
        matchesCustomCatalogId(selected?.key, finishes) ||
        matchesCustomCatalogId(item.product?.finish_related?.id, finishes) ||
        matchesCustomCatalogId(item.finish?.id, finishes)
    );
};

export const isCustomCoatingSelected = (
    item: QuoteLineItemProps,
    coatings?: Array<{ id: number; name: string }>,
    selected?: { key?: string | number; label?: string } | null
) => {
    const selectedLabel = selected?.label;
    const coatingName = firstStringSpec(item.product?.coatings);
    return (
        isCustomLabel(coatingName) ||
        isCustomLabel(item.product?.coating_related?.name) ||
        isCustomLabel(item.coating?.name) ||
        isCustomLabel(selectedLabel) ||
        matchesCustomCatalogId(selected?.key, coatings) ||
        matchesCustomCatalogId(item.product?.coating_related?.id, coatings) ||
        matchesCustomCatalogId(item.coating?.id, coatings)
    );
};
